import React, { useState, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Badge,
  Modal
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import {
  getKitchenDisplayDetails,
  deleteKitchenDisplay
} from '../Controller/KitchenDisplayController'
import Utility from '../../../../GlobalModules/Utility/Utility'
import '../Assets/css/kitchen-display.scss'

const ViewKitchenDisplay = (props) => {
  const { lng, slack } = useParams()
  const history = useHistory()

  const [kitchenDisplay, setKitchenDisplay] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    fetchKitchenDisplayDetails()
  }, [slack])

  const fetchKitchenDisplayDetails = async () => {
    try {
      setLoading(true)
      const response = await getKitchenDisplayDetails(slack)

      if (response.success) {
        setKitchenDisplay(response.data)
      } else {
        Utility.toastNotifications(
          'Failed to load Kitchen Display details',
          'Error',
          'error'
        )
        setError('Failed to load Kitchen Display details')
      }
    } catch (err) {
      Utility.toastNotifications(
        'An error occurred while loading Kitchen Display details',
        'Error',
        'error'
      )
      setError('An error occurred while loading Kitchen Display details')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!kitchenDisplay) return

    setDeleteLoading(true)
    try {
      const response = await deleteKitchenDisplay(kitchenDisplay.slack)

      if (response.success) {
        Utility.toastNotifications(
          'Kitchen Display deleted successfully',
          'Success',
          'success'
        )
        // Redirect to list page after successful deletion
        history.push(`/${lng}/settings/kitchen-displays`)
      } else {
        Utility.toastNotifications(
          response.message || 'Failed to delete Kitchen Display',
          'Error',
          'error'
        )
        setShowDeleteModal(false)
      }
    } catch (err) {
      Utility.toastNotifications(
        'An error occurred while deleting Kitchen Display',
        'Error',
        'error'
      )
      setShowDeleteModal(false)
    } finally {
      setDeleteLoading(false)
    }
  }

  if (loading) {
    return (
      <Container fluid className='p-0'>
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '400px' }}
        >
          <Spinner animation='border' variant='primary' />
          <span className='ms-2'>Loading Kitchen Display details...</span>
        </div>
      </Container>
    )
  }

  if (error) {
    return (
      <Container fluid className='p-0'>
        <Alert variant='danger'>{error}</Alert>
        <Button
          variant='outline-secondary'
          onClick={() => history.push(`/${lng}/settings/kitchen-displays`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
          Back to List
        </Button>
      </Container>
    )
  }

  if (!kitchenDisplay) {
    return (
      <Container fluid className='p-0'>
        <Alert variant='warning'>Kitchen Display not found</Alert>
        <Button
          variant='outline-secondary'
          onClick={() => history.push(`/${lng}/settings/kitchen-displays`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
          Back to List
        </Button>
      </Container>
    )
  }

  //======= add action permission ====//
  // Check multiple permissions at once
  const kitchenDisplayViewPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'kitchen_displays',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>Kitchen Display Details</h4>
        <div className='d-flex gap-2'>
          <Button
            variant='outline-secondary'
            onClick={() => history.push(`/${lng}/settings/kitchen-displays`)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
            Back to List
          </Button>
          {kitchenDisplayViewPermissions.edit && (
            <Button
              variant='primary'
              onClick={() =>
                history.push(`/${lng}/settings/kitchen-displays/edit/${slack}`)
              }
            >
              <FontAwesomeIcon icon={faEdit} className='me-2' />
              Edit
            </Button>
          )}
          {kitchenDisplayViewPermissions.delete && (
            <Button variant='danger' onClick={handleDelete}>
              <FontAwesomeIcon icon={faTrash} className='me-2' />
              Delete
            </Button>
          )}
        </div>
      </div>

      <Card className='custom-border-light'>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <strong>Kitchen Display Code:</strong>
                <p className='mb-0'>
                  {kitchenDisplay.kitchen_display_code || '-'}
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className='mb-3'>
                <strong>Name:</strong>
                <p className='mb-0'>{kitchenDisplay.label || '-'}</p>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <strong>Status:</strong>
                <div className='mt-1'>
                  <Badge
                    bg={kitchenDisplay.status == 1 ? 'success' : 'secondary'}
                  >
                    {kitchenDisplay.status_data?.label ||
                      (kitchenDisplay.status == 1 ? 'Active' : 'Inactive')}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <strong>Orange Timer:</strong>
                <p className='mb-0'>
                  {kitchenDisplay.orange_timer || '10.00'} minutes
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className='mb-3'>
                <strong>Red Timer:</strong>
                <p className='mb-0'>
                  {kitchenDisplay.red_timer || '20.00'} minutes
                </p>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <strong>Created On:</strong>
                <p className='mb-0'>
                  {Utility.get12HourFormat(kitchenDisplay.created_at) || '-'}
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className='mb-3'>
                <strong>Updated On:</strong>
                <p className='mb-0'>
                  {Utility.get12HourFormat(kitchenDisplay.updated_at) || '-'}
                </p>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <strong>Created By:</strong>
                <p className='mb-0'>
                  {kitchenDisplay.created_by?.username || '-'}
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className='mb-3'>
                <strong>Updated By:</strong>
                <p className='mb-0'>
                  {kitchenDisplay.updated_by?.username || '-'}
                </p>
              </div>
            </Col>
          </Row>

          {kitchenDisplay.categories && kitchenDisplay.categories.length > 0 && (
            <Row>
              <Col md={12}>
                <div className='mb-3'>
                  <strong>Categories:</strong>
                  <div className='mt-1'>
                    {(() => {
                      console.log(
                        'Rendering categories:',
                        kitchenDisplay.categories
                      )
                      return kitchenDisplay.categories.map(
                        (category, index) => {
                          console.log(
                            'Rendering category:',
                            category,
                            'Type:',
                            typeof category
                          )
                          return (
                            <Badge
                              key={index}
                              bg='info'
                              className='me-1'
                              style={{ fontSize: '14px', padding: '8px 12px' }}
                            >
                              {typeof category === 'object'
                                ? category.name
                                : `Category ${category}`}
                            </Badge>
                          )
                        }
                      )
                    })()}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this Kitchen Display? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant='danger'
            onClick={confirmDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <Spinner
                  as='span'
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                  className='me-2'
                />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    userEntryType: globalState.LoginReducer.userEntryType
  }
}

export default connect(mapStateToProps)(withNamespaces()(ViewKitchenDisplay))
